/**
 * DateTextInput — one typed date field for dates of birth (Collette Cruse
 * life-ring report 2026-10-16: the browser date control would not take her
 * number-keypad typing; Bill approved 2026-10-16).
 *
 *   - type="text" + inputMode="numeric": any keyboard or keypad works, and
 *     phones show the number pad.
 *   - Mask MM/DD/YYYY with slashes added automatically; a 1- or 2-digit month
 *     and day are both accepted ("5/3/1980" or "05/03/1980").
 *   - onChange receives ISO "YYYY-MM-DD" only when the date is complete and
 *     real, or '' when the field is cleared. Partial typing is held in the
 *     field and never written over the stored value; leaving the field with an
 *     unfinished or impossible date clears the stored value and shows why.
 *
 * Logic lives in utils/dateEntry (unit-tested).
 */
import { useEffect, useRef, useState, type CSSProperties, type FocusEvent } from 'react';
import { formatDateTyping, isoToUsDate, parseUsDate } from '@/utils/dateEntry';
import { toNamedJsonDate } from '@/utils/jxDates';

export interface DateTextInputProps {
  id?: string;
  /** Stored value — ISO "YYYY-MM-DD" (other jxDates-readable shapes are tolerated). */
  value: string | null | undefined;
  /** ISO when complete and valid, '' when cleared or left unfinished. */
  onChange: (iso: string) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  title?: string;
  'aria-label'?: string;
  /** Reject dates after today (default true — this input is for dates of birth). */
  notInFuture?: boolean;
  placeholder?: string;
}

const errorStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--err-text, #b10505)',
  marginTop: 2,
};

export function DateTextInput({
  id,
  value,
  onChange,
  onBlur,
  readOnly,
  disabled,
  className,
  style,
  title,
  'aria-label': ariaLabel,
  notInFuture = true,
  placeholder = 'MM/DD/YYYY',
}: DateTextInputProps): JSX.Element {
  const [text, setText] = useState<string>(() => isoToUsDate(value));
  const [touched, setTouched] = useState<boolean>(false);
  // The ISO this input last reported — a parent echo of it must not reset the typing.
  const lastEmittedRef = useRef<string>(toNamedJsonDate(value ?? ''));

  useEffect(() => {
    const incoming = toNamedJsonDate(value ?? '');
    if (incoming === lastEmittedRef.current) return;
    lastEmittedRef.current = incoming;
    setText(isoToUsDate(value));
    setTouched(false);
  }, [value]);

  const emit = (iso: string): void => {
    if (iso === lastEmittedRef.current) return;
    lastEmittedRef.current = iso;
    onChange(iso);
  };

  const parsed = parseUsDate(text, { notInFuture });
  const message =
    parsed.kind === 'invalid'
      ? parsed.message
      : parsed.kind === 'incomplete' && touched
        ? 'Enter the full date as MM/DD/YYYY.'
        : null;
  const errId = id ? `${id}-err` : undefined;

  return (
    <>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={10}
        className={className}
        style={style}
        title={title}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={text}
        readOnly={readOnly}
        disabled={disabled}
        aria-invalid={message ? true : undefined}
        aria-describedby={message && errId ? errId : undefined}
        onChange={(e) => {
          const next = formatDateTyping(e.currentTarget.value);
          setText(next);
          const p = parseUsDate(next, { notInFuture });
          if (p.kind === 'ok') emit(p.iso);
          else if (p.kind === 'empty') emit('');
          // incomplete / invalid: hold the typing; the stored value is settled on blur.
        }}
        onBlur={(e) => {
          setTouched(true);
          const p = parseUsDate(text, { notInFuture });
          if (p.kind !== 'ok') emit('');
          onBlur?.(e);
        }}
      />
      {message && !readOnly && !disabled && (
        <div id={errId} role="alert" style={errorStyle}>{message}</div>
      )}
    </>
  );
}
